// pages/UserList.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUsers } from "../api";
import CreateUserPopup from "../components/CreateUserPopup";
import UserFilterPopup from "../components/UserFilterPopup";
import { ActionType, ModuleType, PermissionButton } from "components";
import { usePermissions } from "../../../hooks/usePermissions";
import { useRoles } from "../../../hooks/useRoles";
import { Popconfirm } from "antd";
import { deleteUser } from "../api";
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  Pagination,
  Row,
  Col,
  Grid,
  Card,
  Tooltip,
  message,
  theme as antdTheme,
} from "antd";

import { FilterOutlined, CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const UserList = () => {
  const { canViewModule, canViewField } = usePermissions();
  // Users store the role KEY; show the role's display name instead.
  const { byKey: rolesByKey } = useRoles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage(); //Need this as well to show message

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();
  const module = ModuleType.USER;

  // Field-level view permission checks
  const canViewName = canViewField('user', 'name');
  const canViewEmail = canViewField('user', 'email');
  const canViewRole = canViewField('user', 'role');
  const canViewBranch = canViewField('user', 'branch');

  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const requestParams = {
        search: filters.search || "",
        role: filters.role || "",
        status: filters.status || "",
        branch: filters.branch || "",
        sortField: filters.sortField || "",
        sortOrder: filters.sortOrder || "",
        page: pagination.current,
        limit: pagination.pageSize,
      };
      console.log("[UserList] fetchUsers request:", requestParams);
      const response = await fetchUsers(requestParams);
      setUsers(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.meta?.total || response.data.length || 0,
      }));
    } catch (err) {
      console.error("Failed to fetch users", err);
      messageApi.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      const response = await deleteUser(id);
      loadUsers(); // refresh table
      messageApi.success("User Deleted Succesfully");
    } catch (error) {
      console.error("Delete error:", error);
      messageApi.error("Failed to delete User");
    }
  };

  const handleTableChange = (page, pageSize) => {
    setPagination({ ...pagination, current: page, pageSize });
  };

  const handleOpenFilter = () => setFilterVisible(true);
  const handleCloseFilter = () => setFilterVisible(false);

  const handleApplyFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleCloseFilter();
  };

  const handleSearchInputChange = (e) => setSearchTerm(e.target.value);

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchTerm.trim() }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    canViewName && {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: false,
      responsive: ["xs", "sm", "md", "lg"],
    },
    canViewEmail && {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: false,
      responsive: ["sm", "md", "lg"],
    },
    canViewRole && {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) => rolesByKey[text]?.name || text || "-",
      responsive: ["md", "lg"],
    },
    canViewBranch && {
      title: "Branch",
      dataIndex: "branchName",
      key: "branchName",
      render: (text) => text?.toUpperCase() || "-",
      responsive: ["md", "lg"],
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={(e) => {
              e?.stopPropagation(); // prevent triggering row click
              handleDeleteUser(record._id);
            }}
            onClick={(e) => e.stopPropagation()} // optional double safety
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete User">
              <PermissionButton
                buttonType={ActionType.DELETE}
                module={module}
                danger
                onClick={(e) => e.stopPropagation()} // This prevents row click
                size={screens.xs ? "small" : "middle"}
              >
                Delete
              </PermissionButton>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      responsive: ["xs", "sm", "md", "lg"],
    },
  ].filter(Boolean);

  const isFilterApplied = () => {
    // Check if any filter except search is applied
    return (
      filters.role || filters.status || filters.branch || filters.sortField || filters.sortOrder
    );
  };

  const renderMobileCard = (user) => (
    <Card
      key={user._id}
      size="small"
      hoverable
      style={{ marginBottom: 12 }}
      onClick={() => navigate("/users/edit", { state: { userRecord: user } })}
    >
      <Row justify="space-between" align="middle">
        <Col span={16}>
          {canViewName && <Title level={5} style={{ marginBottom: 0 }}>{user.name || "-"}</Title>}
          {canViewEmail && <Text type="secondary" style={{ display: "block" }}>{user.email || "-"}</Text>}
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Space direction="vertical" size={0} style={{ textAlign: "right" }}>
            {canViewRole && <Text style={{ textTransform: "capitalize" }}>{user.role || "-"}</Text>}
            {canViewBranch && <Text type="secondary">{user.branchName ? user.branchName.toUpperCase() : "-"}</Text>}
          </Space>
        </Col>
      </Row>
      <div style={{ marginTop: 8, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
        <Popconfirm
          title="Delete User"
          description={`Are you sure you want to delete "${user.name}"?`}
          okText="Yes, Delete"
          cancelText="Cancel"
          okType="danger"
          onConfirm={() => handleDeleteUser(user._id)}
        >
          <PermissionButton
            buttonType={ActionType.DELETE}
            module={module}
            danger
            size="small"
          >
            Delete
          </PermissionButton>
        </Popconfirm>
      </div>
    </Card>
  );

  if (!canViewModule(module)) {
    return (
      <div style={{ textAlign: "center", marginTop: 60 }}>
        <Text type="danger">You do not have permission to view User</Text>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-4">
                <Col>
                  <Title level={2} style={{ margin: 0 }}>
                    Users Management
                  </Title>
                </Col>
                <Col>
                  <CreateUserPopup reloadUsers={loadUsers} />
                </Col>
              </Row>

              {/* Search + Filter Row */}
              <Row className="mb-3" gutter={[12, 12]}>
                <Col xs={24} sm={18} md={20}>
                  <Input.Search
                    placeholder="Search by Name, Email, or Phone"
                    allowClear
                    enterButton={<SearchOutlined />}
                    size={screens.xs ? "small" : "middle"}
                    value={searchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSearchTerm(val);
                      if (!val.trim()) {
                        setFilters((prev) => ({ ...prev, search: "" }));
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }
                    }}
                    onSearch={handleSearch}
                  />
                </Col>
                <Col xs={24} sm={6} md={4}>
                  <Space wrap style={{ width: "100%", justifyContent: screens.xs ? "space-between" : "flex-end" }}>
                    <Button
                      icon={<FilterOutlined />}
                      onClick={handleOpenFilter}
                      type={isFilterApplied() ? "primary" : "default"}
                      size={screens.xs ? "small" : "middle"}
                    >
                      Filter & Sort
                    </Button>

                    {isFilterApplied() && (
                      <Button
                        type="text"
                        icon={<CloseCircleOutlined style={{ color: "red", fontSize: 18 }} />}
                        onClick={() => {
                          setFilters({ search: filters.search || "" });
                          setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                        title="Remove all filters"
                        size={screens.xs ? "small" : "middle"}
                      />
                    )}
                  </Space>
                </Col>
              </Row>

              {/* Data Section */}
              <Spin spinning={loading}>
                {screens.md ? (
                  <Table
                    columns={columns}
                    dataSource={users}
                    rowKey={(record) => record._id}
                    pagination={false}
                    onRow={(record) => ({
                      onClick: () => navigate("/users/edit", { state: { userRecord: record } }),
                      style: { cursor: "pointer" },
                    })}
                    locale={{ emptyText: "No users found." }}
                    scroll={{ x: "max-content" }}
                    size={screens.lg ? "middle" : "small"}
                  />
                ) : (
                  <div>
                    {users && users.length ? users.map(renderMobileCard) : (
                      <div style={{ textAlign: "center", padding: 24 }}>
                        <Text type="secondary">No users found.</Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Pagination */}
                {pagination.total > 0 && (
                  <Row justify="center" className="mt-4">
                    <Col>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onChange={handleTableChange}
                        onShowSizeChange={handleTableChange}
                        showSizeChanger
                        showQuickJumper={!screens.xs}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} users`}
                        size={screens.xs ? "small" : "default"}
                        responsive
                      />
                    </Col>
                  </Row>
                )}
              </Spin>

              {/* Filters Modal */}
              <UserFilterPopup
                visible={filterVisible}
                onClose={handleCloseFilter}
                onApplyFilters={handleApplyFilters}
                initialFilters={filters}
              />
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserList;
